import axios from 'axios';
import {Alert} from 'react-native';
import {useAuthStore} from '../../store/useAuthStore';

import {Platform} from 'react-native';

const DEV_API_HOST = Platform.OS === 'android' ? '10.0.2.2' : 'localhost';

const API_BASE_URL = __DEV__
  ? `http://${DEV_API_HOST}:8080/api/v1`
  : 'https://app.fabtailor.in/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {'Content-Type': 'application/json'},
  paramsSerializer: {indexes: null},
});

const isAuthEndpoint = url =>
  url?.includes('/auth/login') ||
  url?.includes('/auth/register') ||
  url?.includes('/auth/otp/') ||
  url?.includes('/auth/refresh') ||
  url?.includes('/auth/logout');

// Refresh lock to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({resolve, reject, config}) => {
    if (error) {
      reject(error);
    } else {
      config.headers.Authorization = `Bearer ${token}`;
      resolve(api(config));
    }
  });
  failedQueue = [];
};

// Request interceptor: attach JWT (skip for auth endpoints)
api.interceptors.request.use(
  async config => {
    if (!isAuthEndpoint(config.url)) {
      const token = useAuthStore.getState().accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  error => Promise.reject(error),
);

// Response interceptor: handle 401 and refresh token (skip for auth endpoints)
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({resolve, reject, config: originalRequest});
        });
      }

      isRefreshing = true;

      try {
        await useAuthStore.getState().refreshTokens();
        const newToken = useAuthStore.getState().accessToken;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        await useAuthStore.getState().logout();
        Alert.alert(
          'Session Expired',
          'Your session has expired. Please login again.',
        );
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
